import { useQueryClient } from '@tanstack/react-query';
import { useIntl } from '@mfw/components';

import type {
  CreateExerciseErrorResponse,
  CreateExerciseArgs,
} from '../../api/types/exercises.types';
import type { UiExercise } from '../../schemas/exercises.schema';

import { createExercise } from '../../api/exercises.api';
import { getLabelFromTranslations } from '../../utils';
import { useSnackbar } from '../../context';
import { queryKeys } from '../../queryKeys';
import { useMutation } from '../base';
import messages from './messages';

export default function useCreateExercise() {
  const { enqueueSnackbar } = useSnackbar();
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();
  const response = useMutation<
    UiExercise | null,
    CreateExerciseErrorResponse,
    CreateExerciseArgs
  >({
    mutationFn: (args: CreateExerciseArgs) => createExercise(args),
    onSuccess: async (exercise, { locale, organizationId }) => {
      await queryClient.invalidateQueries({
        exact: false,
        queryKey: queryKeys.exercises.fetchExercises._def,
      });
      await queryClient.invalidateQueries({
        exact: false,
        queryKey: queryKeys.exercises.fetchInfiniteExercises._def,
      });
      if (!exercise?.id) return;
      await queryClient.setQueryData(
        queryKeys.exercises.fetchExerciseById({
          id: exercise.id,
          locale,
          organizationId,
        }).queryKey,
        exercise,
      );
      enqueueSnackbar({
        message: formatMessage(messages.exerciseCreated, {
          name: getLabelFromTranslations(locale, exercise.translations),
        }),
        variant: 'success',
      });
    },
  });
  return {
    ...response,
    isLoading: response.status === 'pending',
  };
}
